import { useEffect, useRef, useState, useCallback } from 'react';

export function useAudio(url: string) {
  const ctxRef = useRef<AudioContext | null>(null);
  const bufferRef = useRef<AudioBuffer | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Precarica il buffer audio (può essere fatto senza gesto utente)
  useEffect(() => {
    // Compatibilità cross-browser
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    
    if (!AudioContextClass) {
      console.error('Web Audio API not supported');
      return;
    }

    ctxRef.current = new AudioContextClass();
    
    // Carica e decodifica l'audio
    fetch(url)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => ctxRef.current!.decodeAudioData(arrayBuffer))
      .then(audioBuffer => {
        bufferRef.current = audioBuffer;
        setLoading(false);
        console.log('Audio buffer loaded successfully');
      })
      .catch(error => {
        console.error('Error loading audio:', error);
        setLoading(false);
      });

    return () => {
      ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
      bufferRef.current = null;
    };
  }, [url]);

  // Sblocca l'audio (da chiamare nel primo gesto utente)
  const unlock = useCallback(async () => {
    if (!ctxRef.current) return false;
    
    if (ctxRef.current.state === 'suspended') {
      try {
        await ctxRef.current.resume();
        console.log('Audio context unlocked');
      } catch (error) {
        console.error('Failed to unlock audio context:', error);
        return false;
      }
    }
    
    setUnlocked(true);
    return true;
  }, []);

  // Play sicuro: da chiamare SOLO dentro un gesto (o dopo unlock riuscito)
  const play = useCallback((options?: { 
    offset?: number; 
    duration?: number; 
    volume?: number 
  }) => {
    if (!ctxRef.current || !bufferRef.current) {
      console.warn('Audio not ready');
      return null;
    }

    const { offset = 0, duration, volume = 1 } = options || {};
    
    try {
      // Crea un nuovo source node per ogni riproduzione
      const source = ctxRef.current.createBufferSource();
      source.buffer = bufferRef.current;
      
      // Crea un gain node per controllare il volume
      const gainNode = ctxRef.current.createGain();
      gainNode.gain.value = volume;
      
      // Connetti: source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(ctxRef.current.destination);
      
      // Avvia la riproduzione
      // start(when, offset, duration)
      source.start(0, offset, duration);
      
      console.log(`Playing audio: offset=${offset}s, duration=${duration}s, volume=${volume}`);
      
      // Ritorna una funzione per fermare l'audio se necessario
      return () => {
        try {
          source.stop();
        } catch (error) {
          // Ignora errori se già fermato
        }
      };
    } catch (error) {
      console.error('Error playing audio:', error);
      return null;
    }
  }, []);

  return { 
    unlocked, 
    unlock, 
    play, 
    loading,
    ready: !loading && bufferRef.current !== null 
  };
}